// src/components/annotation/utils/cursor.ts

// Cache generated cursors by degree + devicePixelRatio
const cursorCache = new Map<string, string>()

function normalizeAngle(rad: number): number {
  // Normalize to [0, Math.PI) because a 180° flip is visually identical
  let a = rad % Math.PI
  if (a < 0) a += Math.PI
  return a
}

export function getRotatedResizeCursor(
  angleRad: number,
  options?: {
    size?: number
    strokeColor?: string
    haloColor?: string
    lineWidth?: number
  }
): { css: string; hotspotX: number; hotspotY: number } | null {
  try {
    const dpr = (globalThis.window?.devicePixelRatio || 1) as number
    const angle = normalizeAngle(angleRad)
    const deg = Math.round((angle * 180) / Math.PI) // 1° granularity
    const sizeCss = Math.max(24, Math.min(64, options?.size || 32)) // CSS pixel size
    const size = Math.round(sizeCss * dpr)
    const key = `${deg}@${dpr}#${size}`
    const cached = cursorCache.get(key)
    if (cached) {
      return {
        css: `url(${cached}) ${Math.floor(sizeCss / 2)} ${Math.floor(sizeCss / 2)}, auto`,
        hotspotX: Math.floor(sizeCss / 2),
        hotspotY: Math.floor(sizeCss / 2),
      }
    }

    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    ctx.save()
    // Draw in device pixels for crispness
    ctx.translate(size / 2, size / 2)
    ctx.rotate(angle)

    const haloColor = options?.haloColor || 'rgba(0,0,0,0.9)'
    const strokeColor = options?.strokeColor || 'rgba(255,255,255,0.95)'
    const lineWidth = (options?.lineWidth || 2) * dpr
    const haloWidth = Math.max(1.5 * dpr, lineWidth * 1.8)

    // Arrow dimensions
    const length = size * 0.6
    const half = length / 2
    const head = Math.max(5 * dpr, size * 0.14)

    // Helper to draw a double headed arrow horizontally centered on origin
    const drawArrow = (lw: number, color: string) => {
      ctx.strokeStyle = color
      ctx.fillStyle = color
      ctx.lineWidth = lw
      ctx.lineCap = 'round'

      // Shaft
      ctx.beginPath()
      ctx.moveTo(-half + head, 0)
      ctx.lineTo(half - head, 0)
      ctx.stroke()

      // Left head
      ctx.beginPath()
      ctx.moveTo(-half, 0)
      ctx.lineTo(-half + head, -head * 0.6)
      ctx.lineTo(-half + head, head * 0.6)
      ctx.closePath()
      ctx.fill()

      // Right head
      ctx.beginPath()
      ctx.moveTo(half, 0)
      ctx.lineTo(half - head, -head * 0.6)
      ctx.lineTo(half - head, head * 0.6)
      ctx.closePath()
      ctx.fill()
    }

    // Halo underlay for visibility
    drawArrow(haloWidth, haloColor)
    // Foreground arrow
    drawArrow(lineWidth, strokeColor)

    ctx.restore()

    const dataUrl = canvas.toDataURL('image/png')
    cursorCache.set(key, dataUrl)
    return {
      css: `url(${dataUrl}) ${Math.floor(sizeCss / 2)} ${Math.floor(sizeCss / 2)}, auto`,
      hotspotX: Math.floor(sizeCss / 2),
      hotspotY: Math.floor(sizeCss / 2),
    }
  } catch (_err) {
    return null
  }
}

// Curved double-arrow rotate cursor (angle-agnostic)
export function getCurvedRotateCursor(options?: {
  size?: number
  strokeColor?: string
  haloColor?: string
  lineWidth?: number
}): { css: string; hotspotX: number; hotspotY: number } | null {
  try {
    const dpr = (globalThis.window?.devicePixelRatio || 1) as number
    const sizeCss = Math.max(24, Math.min(64, options?.size || 32)) // CSS pixel size
    const size = Math.round(sizeCss * dpr)
    const key = `curvedRotate@${dpr}#${size}`
    const cached = cursorCache.get(key)
    if (cached) {
      return {
        css: `url(${cached}) ${Math.floor(sizeCss / 2)} ${Math.floor(sizeCss / 2)}, auto`,
        hotspotX: Math.floor(sizeCss / 2),
        hotspotY: Math.floor(sizeCss / 2),
      }
    }

    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    const haloColor = options?.haloColor || 'rgba(0,0,0,0.9)'
    const strokeColor = options?.strokeColor || 'rgba(255,255,255,0.95)'
    const lineWidth = (options?.lineWidth || 2) * dpr
    const haloWidth = Math.max(1.5 * dpr, lineWidth * 1.8)

    const r = size * 0.28
    const center = { x: size / 2, y: size / 2 }
    const startAngle = -Math.PI * 0.75
    const endAngle = Math.PI * 0.75

    const drawHead = (angle: number, _lw: number, color: string) => {
      const length = Math.max(5 * dpr, size * 0.1)
      const ax = center.x + Math.cos(angle) * r
      const ay = center.y + Math.sin(angle) * r
      const left = angle + Math.PI * 0.6
      const right = angle - Math.PI * 0.6
      ctx.fillStyle = color
      ctx.beginPath()
      ctx.moveTo(ax, ay)
      ctx.lineTo(ax + Math.cos(left) * length, ay + Math.sin(left) * length)
      ctx.lineTo(ax + Math.cos(right) * length, ay + Math.sin(right) * length)
      ctx.closePath()
      ctx.fill()
    }

    const drawArc = (lw: number, color: string) => {
      ctx.strokeStyle = color
      ctx.lineWidth = lw
      ctx.lineCap = 'round'
      ctx.beginPath()
      ctx.arc(center.x, center.y, r, startAngle, endAngle)
      ctx.stroke()
      drawHead(startAngle, lw, color)
      drawHead(endAngle, lw, color)
    }

    // Halo and foreground
    drawArc(haloWidth, haloColor)
    drawArc(lineWidth, strokeColor)

    const dataUrl = canvas.toDataURL('image/png')
    cursorCache.set(key, dataUrl)
    return {
      css: `url(${dataUrl}) ${Math.floor(sizeCss / 2)} ${Math.floor(sizeCss / 2)}, auto`,
      hotspotX: Math.floor(sizeCss / 2),
      hotspotY: Math.floor(sizeCss / 2),
    }
  } catch (_err) {
    return null
  }
}
